import * as React from "react"

import { cn } from "@/lib/utils"
import { THEME, THEME_ORDER, type Theme } from "@/components/login-theme"
import { useTranslation } from "react-i18next"
// 橙色四色轮盘：走打包导入(带 hash)而非 public 绝对路径,避免部署环境取不到静态资源时破图
import themeWheel from "@/assets/images/theme-wheel.svg"

// 视觉对齐登录页 ThemeSwitcher（src/pages/admin/login.tsx）：
// 白底胶囊，当前主题在最左（26px，橙色用 theme-wheel.svg 四色轮盘，其他用纯色大圆点），
// 1×29 浅灰竖线分隔，其余 3 个主题以 24px 纯色圆点排列在右，可点击切换。
const STORAGE_KEY = "eeelt_theme"
const DEFAULT_THEME: Theme = "orange"

type ThemeSelectorProps = {
  className?: string
  compact?: boolean
  /** 兼容旧接口：是否在按钮上方显示「主题」字样（新设计无字样，保留旧 prop 仅用于不破坏调用方） */
  showLabel?: boolean
  /** 受控主题值；提供时组件完全由外部控制 */
  theme?: Theme
  /** 受控模式下主题切换回调 */
  onChange?: (theme: Theme) => void
}

function getInitialTheme(): Theme {
  if (typeof window === "undefined") {
    return DEFAULT_THEME
  }
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return THEME_ORDER.includes(stored as Theme) ? (stored as Theme) : DEFAULT_THEME
}

function Swatch({ theme, size }: { theme: Theme; size: number }) {
  if (theme === "orange") {
    return (
      <img
        src={themeWheel}
        alt=""
        style={{ width: size, height: size, display: "block" }}
      />
    )
  }
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: THEME[theme].dot,
        display: "block",
      }}
    />
  )
}

export default function ThemeSelector({
  className,
  compact = false,
  showLabel: _showLabel = false,
  theme: themeProp,
  onChange,
}: ThemeSelectorProps) {
  const [internalTheme, setInternalTheme] = React.useState<Theme>(getInitialTheme)
  const isControlled = themeProp !== undefined
  const theme = isControlled ? themeProp : internalTheme
  const { t } = useTranslation()

  React.useEffect(() => {
    if (isControlled) return
    document.documentElement.setAttribute("data-theme", theme)
    window.localStorage.setItem(STORAGE_KEY, theme)
  }, [theme, isControlled])

  const setTheme = React.useCallback((next: Theme) => {
    if (isControlled) {
      onChange?.(next)
    } else {
      setInternalTheme(next)
    }
  }, [isControlled, onChange])

  const others = THEME_ORDER.filter((t) => t !== theme)

  // 尺寸按 compact 缩放（compact 下整体缩约 80%），形态/分隔线/顺序均与登录页一致。
  const sizeCurrent = compact ? 22 : 26
  const sizeOther = compact ? 20 : 24
  const dividerH = compact ? 24 : 29
  const gap = compact ? 10 : 13
  const padX = compact ? 13 : 15
  const height = compact ? 32 : 39

  return (
    <div
      role="group"
      aria-label={t('common.themeLabel', { defaultValue: '主題色' })}
      className={cn("inline-flex items-center bg-white shadow-sm", className)}
      style={{
        height,
        gap,
        padding: `0 ${padX}px`,
        borderRadius: 28.5,
      }}
    >
      <button
        type="button"
        aria-label={t('common.theme.current', {
          defaultValue: '當前主題：{{theme}}',
          theme: t(`common.theme.${theme}`, { defaultValue: theme }),
        })}
        aria-pressed
        className="relative inline-flex items-center justify-center rounded-full ring-2 ring-[#64748B] ring-offset-2 ring-offset-white"
        style={{
          border: 0,
          background: "transparent",
          cursor: "default",
          padding: 0,
          lineHeight: 0,
        }}
      >
        <Swatch theme={theme} size={sizeCurrent} />
      </button>
      <span style={{ width: 1, height: dividerH, background: "#F3F3F4" }} />
      {others.map((th) => (
        <button
          key={th}
          type="button"
          onClick={() => setTheme(th)}
          aria-label={t('common.theme.switchTo', {
            defaultValue: '切換到{{theme}}主題',
            theme: t(`common.theme.${th}`, { defaultValue: th }),
          })}
          className="transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-full"
          style={{
            border: 0,
            background: "transparent",
            cursor: "pointer",
            padding: 0,
            lineHeight: 0,
          }}
        >
          <Swatch theme={th} size={sizeOther} />
        </button>
      ))}
    </div>
  )
}
